import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDuration, getSleepStats } from '../utils/dateUtils';

export const SleepStats = ({ entries, childTimezone }) => {
  // Calculate stats from entries
  const stats = getSleepStats(entries, childTimezone);
  const { totalSleep, daySleep, nightSleep, sleepSessions, averageSleep } = stats;

  // Data for pie chart (day vs night sleep)
  const dayNightData = [
    { name: 'Tag', value: daySleep, color: '#fbbf24' },
    { name: 'Nacht', value: nightSleep, color: '#1e293b' }
  ].filter(item => item.value > 0);

  // Data for bar chart (sleep sessions by hour)
  const hourlyData = Array.from({ length: 24 }, (_, hour) => {
    const hourEntries = entries.filter(entry => {
      const entryTime = new Date(entry.startTime || entry.endTime);
      return entryTime.getHours() === hour;
    });
    
    return {
      hour: `${hour}:00`,
      count: hourEntries.length,
      sleep: hourEntries.filter(entry => entry.type === 'start').length,
      wake: hourEntries.filter(entry => entry.type === 'end').length
    };
  });

  const formatTooltip = (value, name) => {
    if (name === 'Tag' || name === 'Nacht') {
      return [formatDuration(value), name];
    }
    return [value, name];
  };

  return (
    <div className="mobile-space-y">
      <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">
        Schlaf-Statistiken
      </h3>

      {/* Summary Cards */}
      <div className="mobile-grid">
        <div className="mobile-card text-center">
          <div className="text-xl sm:text-2xl font-bold text-primary-500">
            {formatDuration(totalSleep)}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Gesamtschlaf
          </div>
        </div>
        
        <div className="mobile-card text-center">
          <div className="text-xl sm:text-2xl font-bold text-blue-500">
            {sleepSessions}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Schlafphasen
          </div>
        </div>
        
        <div className="mobile-card text-center">
          <div className="text-xl sm:text-2xl font-bold text-green-500">
            {formatDuration(averageSleep)}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Ø Dauer
          </div>
        </div>
        
        <div className="mobile-card text-center">
          <div className="text-xl sm:text-2xl font-bold text-purple-500">
            {daySleep > 0 || nightSleep > 0 ? Math.round((nightSleep / (daySleep + nightSleep)) * 100) : 0}%
          </div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Nachtschlaf
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="mobile-space-y">
        {/* Day vs Night Sleep Pie Chart */}
        {dayNightData.length > 0 && (
          <div className="mobile-card">
            <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-4">
              Tag vs. Nachtschlaf
            </h4>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={dayNightData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {dayNightData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={formatTooltip} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 mt-4">
              {dayNightData.map((item, index) => (
                <div key={index} className="flex items-center justify-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                    {item.name}: {formatDuration(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hourly Sleep Pattern Bar Chart */}
        <div className="mobile-card">
          <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-4">
            Schlafmuster nach Uhrzeit
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="hour" 
                tick={{ fontSize: 10 }}
                interval={3}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="sleep" fill="#3b82f6" name="Schlafanfang" />
              <Bar dataKey="wake" fill="#10b981" name="Schlafende" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="mobile-card">
        <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-4">
          Detaillierte Statistiken
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div>
            <div className="text-gray-500 dark:text-gray-400">Tagesschlaf</div>
            <div className="font-medium text-base sm:text-lg">{formatDuration(daySleep)}</div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400">Nachtschlaf</div>
            <div className="font-medium text-base sm:text-lg">{formatDuration(nightSleep)}</div>
          </div>
          <div>
            <div className="text-gray-500 dark:text-gray-400">Schlafqualität</div>
            <div className="font-medium text-base sm:text-lg">
              {totalSleep >= 12 * 60 ? 'Gut' : totalSleep >= 8 * 60 ? 'Normal' : 'Kurz'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 